'use client'

import { useEffect, useMemo, useState } from 'react'

import { useQuery, useZero } from '@rocicorp/zero/react'

import { awaitServerMutation } from '#zero/await-server-mutation.ts'
import { githubRepositoriesFromZero } from '#zero/github-repository-from-zero.ts'
import { mutators } from '#zero/mutators.ts'
import { queries } from '#zero/queries.ts'

export function useGitHubRepositories({
  workspaceId,
  enabled = true,
}: {
  workspaceId: string
  enabled?: boolean
}) {
  const zero = useZero()
  const [refreshError, setRefreshError] = useState<Error | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [repoRows, repoResult] = useQuery(
    queries.github.repositories({ workspaceId }),
    { enabled },
  )
  const repos = useMemo(() => githubRepositoriesFromZero(repoRows), [repoRows])

  useEffect(() => {
    if (!enabled || workspaceId.length === 0) {
      return
    }

    let cancelled = false
    setIsRefreshing(true)
    setRefreshError(null)

    void awaitServerMutation(
      zero.mutate(mutators.github.refreshRepositories({ workspaceId })),
    )
      .catch((error: unknown) => {
        if (!cancelled) {
          setRefreshError(
            error instanceof Error
              ? error
              : new Error('Failed to refresh repositories'),
          )
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsRefreshing(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [enabled, workspaceId, zero])

  return {
    repos,
    isLoading:
      (repoResult.type === 'unknown' || isRefreshing) && repos.length === 0,
    error: refreshError,
  }
}
